import mainApi from './mainApi';

import type { SortType } from '@components/table/hooks/useSort';
import type { Prospect, ProspectDto, Recommendation } from '@lib/types/api/prospect';
import type { Collection } from '@services/utils/buildQueryFilters';
import buildQueryFilters from '@services/utils/buildQueryFilters';
import transformHydraResponse from '@services/utils/transformHydraResponse';
import type { ProspectStatusFormType } from '@view/backoffice/prospect/ProspectGrid';
import type { FormType as ManualContractFormValues } from '@view/backoffice/prospect/manualContractFormSchema';
import type { FormValues } from '@view/subscription/SubscriptionFormHelpers';

const prospectApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    getProspectById: builder.query<Prospect, string>({
      query: (id) => `/public/prospects/${id}`,
      providesTags: ['ProspectsItem'],
    }),
    getProspectDetailsById: builder.query<ProspectDto, string>({
      query: (id) => `/prospects/${id}/details`,
      providesTags: ['ProspectsItem'],
    }),
    patchPublicProspect: builder.mutation<undefined, Prospect>({
      query: ({ id, lastValidatedStep, ...data }) => ({
        body: { ...data, lastValidatedStep },
        method: 'PATCH',
        url: `/public/prospects/${id}`,
      }),
      invalidatesTags: ['ProspectsList', 'ProspectsItem'],
    }),
    getProspectList: builder.query<
      Collection<Prospect>,
      {
        page: number;
        sort: SortType;
        search?: string;
        lastValidatedStep?: number;
        leadStatus?: string;
        nonConversionStatus?: string;
        conversionStatus?: string;
        updatedAtFrom?: string;
        updatedAtTo?: string;
      }
    >({
      query: ({
        page,
        sort,
        lastValidatedStep,
        search,
        leadStatus,
        nonConversionStatus,
        conversionStatus,
        updatedAtFrom,
        updatedAtTo,
      }) => ({
        url: `/prospects`,
        params: buildQueryFilters(sort, {
          lastValidatedStep,
          search,
          leadStatus,
          nonConversionStatus,
          conversionStatus,
          'updatedAt[after]': updatedAtFrom,
          'updatedAt[strictly_before]': updatedAtTo,
          page,
          itemsPerPage: 10,
        }),
      }),
      providesTags: ['ProspectsList'],
      transformResponse: transformHydraResponse,
    }),
    postProspectRecommendations: builder.query<Collection<Recommendation>, { id: string }>({
      query: ({ id }) => ({
        body: {},
        method: 'POST',
        url: `/public/prospects/${id}/recommendations`,
      }),
      transformResponse: transformHydraResponse,
      providesTags: ['Recommendations'],
    }),
    postProspectSetOfffer: builder.mutation<
      Prospect,
      {
        id: string;
        data: {
          formulaName: string;
          price: number;
          provider: string;
          formulaId: number;
          gammeId: number;
          discount?: number;
        };
      }
    >({
      query: ({ id, data }) => ({
        body: data,
        method: 'POST',
        url: `/public/prospects/${id}/set_offer`,
      }),
      invalidatesTags: ['ProspectsItem'],
    }),
    putPublicProspect: builder.mutation<undefined, { id: string; data: FormValues }>({
      query: ({ id, data }) => ({
        // Because we need to send the data as a JSON-LD we need to stringify it
        body: JSON.stringify({
          '@id': `/api/public/prospects/${id}`,
          lastValidatedStep: 6,
          manualContracts: [data],
        }),
        method: 'PUT',
        url: `/public/prospects/${id}`,
        headers: {
          'Content-Type': 'application/ld+json',
        },
      }),
      invalidatesTags: ['ProspectsList', 'ProspectsItem'],
    }),

    patchProspect: builder.mutation<undefined, Partial<Prospect>>({
      query: ({ id, lastValidatedStep, ...data }) => ({
        body: { ...data, lastValidatedStep },
        method: 'PATCH',
        url: `/prospects/${id}`,
      }),
      invalidatesTags: ['ProspectsList', 'ProspectsItem'],
    }),
    putProspectManualContract: builder.mutation<undefined, { id: string; data: ManualContractFormValues }>({
      query: ({ id, data }) => ({
        // Because we need to send the data as a JSON-LD we need to stringify it
        body: JSON.stringify({
          '@id': `/api/prospects/${id}`,
          manualContracts: [
            {
              '@id': data.id,
              ...data,
            },
          ],
        }),
        method: 'PUT',
        url: `/prospects/${id}`,
        headers: {
          'Content-Type': 'application/ld+json',
        },
      }),
    }),
    putProspectStatus: builder.mutation<undefined, { id: string; data: ProspectStatusFormType }>({
      query: ({ id, data }) => ({
        body: JSON.stringify({
          '@id': `/api/prospects/${id}`,
          ...data,
        }),
        method: 'PUT',
        url: `/prospects/${id}`,
        headers: {
          'Content-Type': 'application/ld+json',
        },
      }),
      invalidatesTags: ['ProspectsList', 'ProspectsItem'],
    }),
  }),
});

export default prospectApi;

export const {
  useGetProspectByIdQuery,
  usePatchPublicProspectMutation,
  useGetProspectListQuery,
  usePostProspectRecommendationsQuery,
  usePostProspectSetOffferMutation,
  useLazyGetProspectByIdQuery,
  useGetProspectDetailsByIdQuery,
  usePutPublicProspectMutation,
  usePatchProspectMutation,
  usePutProspectManualContractMutation,
  usePutProspectStatusMutation,
} = prospectApi;
